import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createLocalAudioTrack,
  createLocalVideoTrack,
  Track,
  TrackEvent,
  type LocalAudioTrack,
  type LocalVideoTrack,
} from 'livekit-client';
import { useLocalParticipant } from '@livekit/components-react';
import { nextSourceIndex } from '@liveforge/shared';
import { describeDeviceError } from '../lib/media';
import { useStudioStore } from '../stores/studio-store';

/**
 * One local capture source: a video device, and optionally an audio device
 * captured alongside it. A capture card presents itself as a webcam, so it
 * needs no special handling here.
 */
export interface LocalSource {
  id: string;
  /**
   * Numbers the LiveKit track names (`cam-1`, `mic-1`). It never reuses a
   * value, so removing a source cannot rename the ones that outlive it - a
   * remote layout keyed on the name would otherwise follow the wrong camera.
   */
  index: number;
  label: string;
  videoDeviceId: string;
  audioDeviceId: string | null;
  videoTrack: LocalVideoTrack;
  audioTrack: LocalAudioTrack | null;
  videoEnabled: boolean;
  audioEnabled: boolean;
  /** Set when the device disappeared while it was publishing. */
  lost: boolean;
}

export interface AddSourceInput {
  label: string;
  videoDeviceId: string;
  /** `null` publishes video only - the spec's "none" audio option. */
  audioDeviceId: string | null;
}

/**
 * Reattaching a source the studio remembers, rather than adding a new one.
 *
 * The index has to be the one it had: a layer placed in a scene points at
 * `cam-2` by name, so a camera that comes back as `cam-3` leaves that layer
 * drawing a placeholder while the picture appears somewhere nobody asked for.
 */
export interface RestoreSource {
  index: number;
}

/**
 * Publishes additional camera and microphone tracks beyond the host's own
 * camera, so several angles reach the room at once (Phase 2).
 *
 * **These are published as `Track.Source.Unknown`, never `Camera`.** LiveKit's
 * `setCameraEnabled` resolves its target with `getTrackPublication(source)`,
 * which returns the *first* publication carrying that source - so a second
 * track claiming `Camera` would make the host's own camera button act on an
 * arbitrary one of them, depending on Map insertion order. The tracks are told
 * apart by name instead, which is what `phase-02.md` specifies anyway.
 */
export function useLocalSources() {
  const { localParticipant } = useLocalParticipant();
  const [sources, setSources] = useState<LocalSource[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const nextIndex = useRef(1);
  /**
   * One above the highest device index the Live has ever used, restored
   * sources included. Raised, never lowered: a manifest that arrives late must
   * push new sources past the numbers it holds, and two sources added before
   * the dock has reported either of them must still not collide.
   */
  const floor = useStudioStore((state) =>
    nextSourceIndex(
      [...state.attachedSources, ...state.pendingSources].filter(
        (entry) => entry.kind === 'device',
      ),
    ),
  );
  useEffect(() => {
    if (floor > nextIndex.current) nextIndex.current = floor;
  }, [floor]);
  // Mirrors `sources` so the unmount cleanup and the async callbacks can read
  // the current list without being rebuilt on every change - an unmount has to
  // release every track that exists at that moment, not the ones captured when
  // the effect was created. Written in an effect, never during render: React
  // forbids mutating a ref while rendering, and the callbacks that read it all
  // run from user events, after this has committed.
  const live = useRef<LocalSource[]>([]);
  useEffect(() => {
    live.current = sources;
  }, [sources]);

  const markLost = useCallback((id: string) => {
    setSources((current) =>
      current.map((source) => (source.id === id ? { ...source, lost: true } : source)),
    );
  }, []);

  /** Answers whether the source is publishing, which a restore has to know. */
  const addSource = useCallback(
    async (input: AddSourceInput, restore?: RestoreSource): Promise<boolean> => {
      setError(null);
      setBusy(true);

      const index = restore?.index ?? nextIndex.current;
      let videoTrack: LocalVideoTrack | undefined;
      let audioTrack: LocalAudioTrack | undefined;

      try {
        videoTrack = await createLocalVideoTrack({ deviceId: { exact: input.videoDeviceId } });
        if (input.audioDeviceId) {
          audioTrack = await createLocalAudioTrack({ deviceId: { exact: input.audioDeviceId } });
        }

        await localParticipant.publishTrack(videoTrack, {
          name: `cam-${index}`,
          source: Track.Source.Unknown,
        });
        if (audioTrack) {
          await localParticipant.publishTrack(audioTrack, {
            name: `mic-${index}`,
            source: Track.Source.Unknown,
          });
        }
      } catch (cause) {
        // Publishing the audio can fail after the video is already live, so
        // whatever was acquired has to be released rather than left running
        // with the camera light on and nothing rendering it.
        if (videoTrack) {
          await localParticipant.unpublishTrack(videoTrack, true).catch(() => undefined);
          videoTrack.stop();
        }
        if (audioTrack) {
          await localParticipant.unpublishTrack(audioTrack, true).catch(() => undefined);
          audioTrack.stop();
        }
        setError(describeDeviceError(cause, audioTrack ? 'microphone' : 'camera'));
        setBusy(false);
        return false;
      }

      if (!restore) nextIndex.current += 1;
      // Derived from the index, so a restored source comes back under the
      // identity every scene already refers to.
      const id = `source-${index}`;

      // A device unplugged mid-session ends its track. Flagging the source
      // keeps it on screen with an explanation instead of leaving a frozen
      // tile the host cannot account for.
      videoTrack.once(TrackEvent.Ended, () => markLost(id));
      audioTrack?.once(TrackEvent.Ended, () => markLost(id));

      setSources((current) => [
        ...current,
        {
          id,
          index,
          label: input.label.trim() || `Camera ${index}`,
          videoDeviceId: input.videoDeviceId,
          audioDeviceId: input.audioDeviceId,
          videoTrack,
          audioTrack: audioTrack ?? null,
          videoEnabled: true,
          audioEnabled: audioTrack !== undefined,
          lost: false,
        },
      ]);
      setBusy(false);
      return true;
    },
    [localParticipant, markLost],
  );

  const removeSource = useCallback(
    async (id: string) => {
      const source = live.current.find((candidate) => candidate.id === id);
      if (!source) return;

      setSources((current) => current.filter((candidate) => candidate.id !== id));

      await localParticipant.unpublishTrack(source.videoTrack, true).catch(() => undefined);
      source.videoTrack.stop();
      if (source.audioTrack) {
        await localParticipant.unpublishTrack(source.audioTrack, true).catch(() => undefined);
        source.audioTrack.stop();
      }
    },
    [localParticipant],
  );

  /**
   * Mutes rather than unpublishes. Unpublishing would tear the tile out of
   * every remote grid and force a renegotiation to bring it back; muting keeps
   * the slot, which is what a director toggling a camera expects.
   */
  const setVideoEnabled = useCallback(async (id: string, enabled: boolean) => {
    const source = live.current.find((candidate) => candidate.id === id);
    if (!source) return;

    await (enabled ? source.videoTrack.unmute() : source.videoTrack.mute());
    setSources((current) =>
      current.map((candidate) =>
        candidate.id === id ? { ...candidate, videoEnabled: enabled } : candidate,
      ),
    );
  }, []);

  const setAudioEnabled = useCallback(async (id: string, enabled: boolean) => {
    const source = live.current.find((candidate) => candidate.id === id);
    if (!source?.audioTrack) return;

    await (enabled ? source.audioTrack.unmute() : source.audioTrack.mute());
    setSources((current) =>
      current.map((candidate) =>
        candidate.id === id ? { ...candidate, audioEnabled: enabled } : candidate,
      ),
    );
  }, []);

  /**
   * Hot-swap. `restartTrack` swaps the underlying capture device inside the
   * existing publication, so the track keeps its sid and name and no other
   * source is disturbed - unpublishing and republishing would drop the tile
   * and renumber nothing but still flicker every remote grid.
   */
  const swapVideoDevice = useCallback(
    async (id: string, deviceId: string) => {
      const source = live.current.find((candidate) => candidate.id === id);
      if (!source) return;

      setError(null);
      try {
        await source.videoTrack.restartTrack({ deviceId: { exact: deviceId } });
      } catch (cause) {
        setError(describeDeviceError(cause, 'camera'));
        return;
      }

      source.videoTrack.once(TrackEvent.Ended, () => markLost(id));
      setSources((current) =>
        current.map((candidate) =>
          candidate.id === id
            ? { ...candidate, videoDeviceId: deviceId, lost: false, videoEnabled: true }
            : candidate,
        ),
      );
    },
    [markLost],
  );

  /**
   * Swapping audio has a case video does not: a source created with no audio
   * has no track to restart, so the first real device selection has to create
   * and publish one. Selecting "none" goes the other way and unpublishes it.
   */
  const swapAudioDevice = useCallback(
    async (id: string, deviceId: string | null) => {
      const source = live.current.find((candidate) => candidate.id === id);
      if (!source) return;

      setError(null);

      if (deviceId === null) {
        if (source.audioTrack) {
          await localParticipant.unpublishTrack(source.audioTrack, true).catch(() => undefined);
          source.audioTrack.stop();
        }
        setSources((current) =>
          current.map((candidate) =>
            candidate.id === id
              ? { ...candidate, audioTrack: null, audioDeviceId: null, audioEnabled: false }
              : candidate,
          ),
        );
        return;
      }

      if (source.audioTrack) {
        try {
          await source.audioTrack.restartTrack({ deviceId: { exact: deviceId } });
        } catch (cause) {
          setError(describeDeviceError(cause, 'microphone'));
          return;
        }
        setSources((current) =>
          current.map((candidate) =>
            candidate.id === id ? { ...candidate, audioDeviceId: deviceId } : candidate,
          ),
        );
        return;
      }

      let created: LocalAudioTrack | undefined;
      try {
        created = await createLocalAudioTrack({ deviceId: { exact: deviceId } });
        await localParticipant.publishTrack(created, {
          name: `mic-${source.index}`,
          source: Track.Source.Unknown,
        });
      } catch (cause) {
        if (created) {
          await localParticipant.unpublishTrack(created, true).catch(() => undefined);
          created.stop();
        }
        setError(describeDeviceError(cause, 'microphone'));
        return;
      }

      created.once(TrackEvent.Ended, () => markLost(id));
      setSources((current) =>
        current.map((candidate) =>
          candidate.id === id
            ? {
                ...candidate,
                audioTrack: created ?? null,
                audioDeviceId: deviceId,
                audioEnabled: true,
              }
            : candidate,
        ),
      );
    },
    [localParticipant, markLost],
  );

  // Leaving the studio must release every camera and microphone this hook
  // opened. Nothing else stops them: the tracks are owned here, not by the
  // Room, so disconnecting alone would leave the capture lights on.
  useEffect(() => {
    return () => {
      for (const source of live.current) {
        source.videoTrack.stop();
        source.audioTrack?.stop();
      }
    };
  }, []);

  return {
    sources,
    error,
    busy,
    addSource,
    removeSource,
    setVideoEnabled,
    setAudioEnabled,
    swapVideoDevice,
    swapAudioDevice,
    clearError: useCallback(() => setError(null), []),
  };
}
